import { ParsedUrlQuery } from 'querystring';

import { GetStaticProps } from 'next';
import NextImage from 'next/image';
import { useTranslation } from 'next-i18next';

import { PUBLIC_IMAGES } from '../../../config';

import { Layout } from '@/components/Layout';
import { Button } from '@/components/shared/Button';
import { EmptyContent } from '@/components/shared/EmptyContent';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import { Image } from '@/components/shared/Image';
import Link from '@/components/shared/Link';
import { PageHeading } from '@/components/shared/PageHeading';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import getPageBanner from '@/helpers/getPageBanner';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { COMMON_LOCALE_PART, VOLUNTEER_LOCALE_PART } from '@/locale';
import { Colors, IPage, Locale, SiteMenuItem } from '@/types';
import { IBlogDetailsProps } from '@/types/common';
import { formatPathname } from '@/helpers/routing';
import { BlogDetails } from '@/types/types/Pages';

export default function BlogPage({ siteMenu, pageDetails }: IBlogDetailsProps) {
  const { t } = useTranslation([COMMON_LOCALE_PART, VOLUNTEER_LOCALE_PART]);

  if (!pageDetails) {
    return <EmptyContent />;
  }

  return (
    <Layout
      siteMenu={siteMenu}
      seoOptions={{
        title: pageDetails?.name,
        description: 'description',
        thumbnail: pageDetails?.banner,
      }}
      banner={getPageBanner(pageDetails)}
    >
      <div className="container">
        <PageHeading>{pageDetails.name}</PageHeading>
        <p className='mb-8'>{pageDetails.description}</p>
        <div className="mb-18 flex flex-col justify-start gap-y-15">
          {pageDetails?.posts?.map(({ page_slug, img, img_optimized, name, body }) => (
            <div key={page_slug} className="flex w-full flex-col md:flex-row md:justify-start md:gap-x-10">
              <Image
                className="h-[220px] w-full rounded-lg object-cover md:aspect-square md:h-[unset] md:w-66"
                optimized={img_optimized}
                src={img}
                alt={name || pageDetails?.name}
              />
              <div className="flex w-full flex-col justify-between">
                <div>
                  <h3 className="mb-3.5 mt-9.5 md:mt-0">{name}</h3>
                  <p className="mb-8 text-ellipsis md:mb-0 md:line-clamp-3 lg:line-clamp-4">{body}</p>
                </div>
                <Link href={formatPathname(page_slug)}>
                  <Button className="flex items-center [&>svg]:ml-4" variant={Colors.primary}>
                    {t('buttons.find-out-more')}{' '}
                    {Icons({ name: IconsTypes.ARROW_RIGHT, iconProps: { className: 'text-white-600' } })}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mx-auto w-full max-w-full md:max-w-full lg:w-[97%] lg:max-w-[1140px]">
        <div className="mx-auto flex w-full max-w-full lg:mb-15 lg:justify-end">
          <div className=" flex max-w-full flex-col justify-start bg-green lg:flex-row lg:rounded-3">
            <div className="w-full p-4 md:p-8 lg:w-[calc(100%-368px)] lg:p-10">
              <h3 className="text-white md:leading-9">{t(VOLUNTEER_LOCALE_PART + ':' + 'volunteer.title')}</h3>
              <p
                className="mb-9.5 mt-3.5 text-white"
                dangerouslySetInnerHTML={{ __html: t(VOLUNTEER_LOCALE_PART + ':' + 'volunteer.description') ?? '' }}
              />
              <Link href={'https://forms.gle/hjMPB14gd7qGuf2EA'}>
                <Button
                  className="flex items-center [&>svg]:ml-4"
                  variant="bg-[#4FB8A5] text-white hover:bg-[#4FB8A590]"
                >
                  {t(VOLUNTEER_LOCALE_PART + ':' + 'volunteer.fill-form')}{' '}
                  {Icons({ name: IconsTypes.ARROW_RIGHT, iconProps: { className: 'text-white-600' } })}
                </Button>
              </Link>
            </div>
            <div className="relative h-[240px] w-full md:h-[325px] lg:h-full lg:w-[368px] lg:rounded-3">
              <NextImage
                fill
                alt={t(VOLUNTEER_LOCALE_PART + ':' + 'volunteer.title') as string}
                src={PUBLIC_IMAGES.VOLUNTEERS}
                className="absolute h-full w-full object-cover lg:rounded-3"
              />
              <div
                style={{ background: 'linear-gradient(270deg, rgba(36, 126, 128, 0) 60.56%, #247E80 100%)' }}
                className="absolute hidden h-full w-full lg:block"
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

interface IParams extends ParsedUrlQuery {
  locale: Locale;
  // blog_slug: string;
}

export async function getStaticPaths() {
  // const request = api({});
  // const pages = await request<IPage[]>('blogs/');

  // const pagesMapped = pages.data
  //   .map((page) => ({
  //     params: {
  //       blog_slug: page.slug,
  //     },
  //   }));

  const paths = getI18nPaths()
    // .map(({ params: paramsLocale }) => {
    //   return pagesMapped.map(({ params: paramsPaths }) => ({
    //     params: { ...paramsPaths, ...paramsLocale },
    //   }));
    // })
    // .flat();

  return {
    paths,
    fallback: false,
  };
}

interface BlogDetailsStaticProps extends GetStaticProps {
  locale: Locale;
  params: IParams;
}

export const getStaticProps = async ({ params }: BlogDetailsStaticProps) => {
  const { locale, blog_slug = 'help-us' } = params as IParams;
  const request = api({ locale: locale });

  const responses = await Promise.allSettled([
    request<SiteMenuItem[]>('site-menu/'),
    request<BlogDetails>(`blogs/detail/${blog_slug}`),
  ]);
  const values = responses.map((val) => (val.status === 'fulfilled' ? val.value.data : undefined));
  const [siteMenu, pageDetails] = values;

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART, VOLUNTEER_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu as SiteMenuItem[]),
      pageDetails,
    },
  };
};
